import { Component, Input, OnInit, OnDestroy, DoCheck } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomMessageService } from '../../../core/services/custom-message.service';

export interface CustomMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
}

@Component({
  selector: 'app-custom-message',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="custom-message-container" *ngIf="messages.length > 0">
      <div 
        *ngFor="let message of messages" 
        class="custom-message"
        [ngClass]="'message-' + message.type"
        [@slideInOut]>
        <div class="message-content">
          <div class="message-icon">
            <i [class]="getMessageIcon(message.type)"></i>
          </div>
          <div class="message-text">
            <div class="message-title">{{ message.title }}</div>
            <div class="message-detail">{{ message.message }}</div>
          </div>
          <button 
            class="message-close" 
            (click)="removeMessage(message.id)"
            aria-label="Close message">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <div 
          class="message-progress" 
          *ngIf="message.duration && message.duration > 0"
          [style.animation-duration]="message.duration + 'ms'">
        </div>
      </div>
    </div>
  `,
  styles: [`
    .custom-message-container {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 1000;
      max-width: 400px;
      pointer-events: none;
    }

    .custom-message {
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      margin-bottom: 12px;
      overflow: hidden;
      pointer-events: auto;
      position: relative;
      animation: slideInRight 0.3s ease-out;
    }

    .message-content {
      display: flex;
      align-items: flex-start;
      padding: 16px;
      gap: 12px;
    }

    .message-icon {
      flex-shrink: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
    }

    .message-text {
      flex: 1;
      min-width: 0;
    }

    .message-title {
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 4px;
      color: #1f2937;
    }

    .message-detail {
      font-size: 13px;
      color: #6b7280;
      line-height: 1.4;
    }

    .message-close {
      background: none;
      border: none;
      color: #9ca3af;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.2s;
      flex-shrink: 0;
    }

    .message-close:hover {
      background: #f3f4f6;
      color: #374151;
    }

    .message-progress {
      position: absolute;
      bottom: 0;
      left: 0;
      height: 3px;
      background: rgba(0, 0, 0, 0.1);
      animation: progressBar linear forwards;
    }

    /* Message type styles */
    .message-success {
      border-left: 4px solid #10b981;
    }

    .message-success .message-icon {
      background: #d1fae5;
      color: #10b981;
    }

    .message-success .message-progress {
      background: #10b981;
    }

    .message-error {
      border-left: 4px solid #ef4444;
    }

    .message-error .message-icon {
      background: #fee2e2;
      color: #ef4444;
    }

    .message-error .message-progress {
      background: #ef4444;
    }

    .message-warning {
      border-left: 4px solid #f59e0b;
    }

    .message-warning .message-icon {
      background: #fef3c7;
      color: #f59e0b;
    }

    .message-warning .message-progress {
      background: #f59e0b;
    }

    .message-info {
      border-left: 4px solid #3b82f6;
    }

    .message-info .message-icon {
      background: #dbeafe;
      color: #3b82f6;
    }

    .message-info .message-progress {
      background: #3b82f6;
    }

    /* Animations */
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes progressBar {
      from {
        width: 100%;
      }
      to {
        width: 0%;
      }
    }

    @keyframes slideInOut {
      0% {
        transform: translateX(100%);
        opacity: 0;
      }
      10% {
        transform: translateX(0);
        opacity: 1;
      }
      90% {
        transform: translateX(0);
        opacity: 1;
      }
      100% {
        transform: translateX(100%);
        opacity: 0;
      }
    }
  `],
  animations: [
    // Add Angular animations if needed
  ]
})
export class CustomMessageComponent implements OnInit, OnDestroy, DoCheck {
  @Input() messages: CustomMessage[] = [];
  
  private messageTimeouts: Map<string, any> = new Map();
  private processedMessageIds: Set<string> = new Set();

  constructor(private customMessageService: CustomMessageService) {}

  ngOnInit() {
    // Auto-remove messages with duration
    this.messages.forEach(message => {
      if (message.duration && message.duration > 0) {
        this.setMessageTimeout(message);
        this.processedMessageIds.add(message.id);
      }
    });
  }

  ngDoCheck() {
    // Check for new messages that haven't been processed yet
    this.messages.forEach(message => {
      if (!this.processedMessageIds.has(message.id) && message.duration && message.duration > 0) {
        this.setMessageTimeout(message);
        this.processedMessageIds.add(message.id);
      }
    });

    // Clean up processed IDs for messages that no longer exist
    const currentIds = new Set(this.messages.map(m => m.id));
    this.processedMessageIds.forEach(id => {
      if (!currentIds.has(id)) {
        this.processedMessageIds.delete(id);
      }
    });
  }

  ngOnDestroy() {
    // Clear all timeouts
    this.messageTimeouts.forEach(timeout => clearTimeout(timeout));
    this.messageTimeouts.clear();
    this.processedMessageIds.clear();
  }

  getMessageIcon(type: string): string {
    switch (type) {
      case 'success': return 'pi pi-check-circle';
      case 'error': return 'pi pi-times-circle';
      case 'warning': return 'pi pi-exclamation-triangle';
      case 'info': return 'pi pi-info-circle';
      default: return 'pi pi-info-circle';
    }
  }

  removeMessage(messageId: string) {
    // Clear timeout if exists
    const timeout = this.messageTimeouts.get(messageId);
    if (timeout) {
      clearTimeout(timeout);
      this.messageTimeouts.delete(messageId);
    }
    
    // Remove from processed set
    this.processedMessageIds.delete(messageId);
    
    // Remove from service (which will update the messages array)
    this.customMessageService.removeMessage(messageId);
  }

  private setMessageTimeout(message: CustomMessage) {
    const timeout = setTimeout(() => {
      this.removeMessage(message.id);
    }, message.duration);
    
    this.messageTimeouts.set(message.id, timeout);
  }
}
